/**
 * Category-page tile enhancer — the vanilla replacement for the retired
 * React tile carousel (tiles plan U2). Progressive enhancement only: the
 * page is complete server-rendered HTML before this runs, and every failure
 * path leaves that HTML untouched.
 *
 * Two triggers, deliberately split: the slide fragment is FETCHED when a
 * tile approaches the viewport, and Swiper is MOUNTED on first interaction
 * intent. Mounting 24 Swiper instances on scroll would undo the very
 * performance work this template just gained.
 */
import Carousel from "../lazyCarousels/Carousel";
import lazyImages from "../lazyImages/lazyImages";
import loadStylesheet from "../loadStylesheet";
import "../location/copyUrl";
import openGalleryModal from "./galleryModal";

const TILE_SELECTOR = ".il-location-carousel[data-location-slug]";

/**
 * Hub tiles get click arrows: a desktop mouse without a trackpad has no
 * horizontal-swipe gesture, and the wheel scrolls the page (William,
 * 2026-08-20). Created here rather than in the slides fragment so the
 * shared category markup stays untouched; hub-only via the same
 * .il-hub-tiles gate the hub tile styles hang off (ALL hub pages, incl. pagination), and the buttons are
 * per-tile elements so 24 tiles can't cross-bind through a global
 * selector. Styled in the hub style block (hub-hero.blade.php).
 *
 * @param {Element} tile
 * @returns {object|false} Swiper navigation options, or false off-hub
 */
function tileNavigation(tile) {
    if (!document.querySelector(".il-hub-tiles")) return false;

    // A retired tile re-mounting keeps its original buttons — creating a
    // second pair would stack two arrow sets on one photo.
    const existingPrev = tile.querySelector(".il-tile-nav--prev");
    if (existingPrev) {
        return {
            prevEl: existingPrev,
            nextEl: tile.querySelector(".il-tile-nav--next"),
            disabledClass: "il-tile-nav--disabled",
        };
    }

    const make = (dir) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "il-tile-nav il-tile-nav--" + dir;
        btn.setAttribute("aria-label", dir === "prev" ? "Previous image" : "Next image");
        btn.innerHTML =
            '<svg width="9" height="14" viewBox="0 0 9 14" aria-hidden="true" focusable="false">' +
            '<path d="M1.5 1 7.5 7l-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        tile.appendChild(btn);
        return btn;
    };

    return {
        prevEl: make("prev"),
        nextEl: make("next"),
        disabledClass: "il-tile-nav--disabled",
    };
}

/**
 * Swiper options for a tile-sized carousel. Fully specified rather than
 * merged from the shared defaults: options.js mutates its own exported
 * _default via Object.assign, and its scrollbar/navigation selectors are
 * document-global — with 24 tiles on a page, inherited selectors would
 * cross-bind every tile to one element.
 *
 * @param {Element} tile
 * @returns {object}
 */
function tileOptions(tile) {
    const scrollbar = tile.querySelector(".swiper-scrollbar");

    return {
        // One slide per tile, not "auto" — except on the hub, where the
        // filmstrip design needs per-photo widths. The zero-measurement
        // trap that ruled "auto" out for everyone else doesn't apply
        // there: hub slide widths are CSS values computed from the
        // server-stamped --il-ar ratio (hub-hero.blade.php), never from
        // the image's loaded size, so a re-measure before images load
        // still reads real widths. Off-hub slides carry no width CSS, so
        // "auto" would still measure intrinsic near-zero — keep 1.
        slidesPerView: document.querySelector(".il-hub-tiles") ? "auto" : 1,
        grabCursor: true,
        passiveListeners: true,
        spaceBetween: 4,
        // Real flick physics: drag freely with momentum carry, then settle on
        // a whole photo instead of stopping mid-image.
        freeMode: {
            enabled: true,
            sticky: true,
            momentum: true,
            momentumRatio: 1.4,
            momentumVelocityRatio: 1.2,
        },
        // forceToAxis keeps a horizontal swipe from stealing vertical page
        // scroll — the mobile behavior the split-trigger design adds.
        mousewheel: { forceToAxis: true },
        navigation: tileNavigation(tile),
        scrollbar: scrollbar ? { el: scrollbar, draggable: true, hide: false } : false,
        // A neighbouring tile finishing its own upgrade reflows the grid,
        // which fired this tile's element resize observer and reset its
        // position. Tile geometry is fixed after mount, so the per-element
        // observer buys nothing; genuine window resizes still update.
        resizeObserver: false,
        observer: false,
        observeParents: false,
    };
}

/**
 * Swap the static feature-image anchor for the fetched slide markup. The
 * fragment carries the feature image as slide 1, so the replacement is
 * wholesale — no dedupe heuristics, and slide 1 gains the overlay buttons
 * the static anchor never had.
 *
 * @param {Element} tile
 * @param {string} html
 * @returns {void}
 */
function injectSlides(tile, html) {
    // Hold the rendered height across the swap so the upgrade cannot shift
    // the grid.
    const height = tile.offsetHeight;
    if (height) {
        tile.style.minHeight = height + "px";
    }

    tile.innerHTML = html;
    tile.classList.add("swiper");
    tile.dataset.tileUpgraded = "1";

    // The share button is CSS-hidden until something marks it ready — a hook
    // the lazy-image loader fires on each image's own onload. Slides the
    // visitor never scrolls to never load, so most share buttons would stay
    // invisible. These are server-rendered with real media ids, so they are
    // ready the moment they exist.
    tile.querySelectorAll(".il-share-image-btn").forEach((btn) => {
        btn.classList.add("il-share-ready");
    });

    // The slides carry lazy data-src placeholders, and Swiper's own image
    // loader does not run until mount — which is deliberately deferred to
    // first interaction. Without this the tile would sit blank in the
    // meantime, which is worse than the static image it replaced.
    lazyImages(tile.querySelectorAll("img.carousel-lazy-img"));
}

/**
 * Live-instance pool (William, 2026-08-20): mount-on-intent already keeps
 * Swiper instances scarce, but nothing ever RETIRED them — a visitor
 * mousing down the grid accumulated one live instance per tile touched,
 * for the life of the page. The pool caps concurrency: when a mount would
 * exceed it, the longest-mounted instance is destroyed. Its slide DOM and
 * loaded images stay (a re-mount is instant and cheap), and the tile is
 * re-armed for intent so hovering it again simply mounts it back.
 */
const MOUNTED_MAX = 8;
const mountedTiles = [];

function retireOverflow() {
    while (mountedTiles.length > MOUNTED_MAX) {
        const oldest = mountedTiles.shift();
        try {
            if (oldest.carousel && oldest.carousel.swiper) {
                // cleanStyles: the wrapper's stale translate would otherwise
                // freeze the strip mid-scroll with no controller attached.
                oldest.carousel.swiper.destroy(true, true);
            }
        } catch (e) {
            // A tile mid-teardown must never break the pool for the rest.
        }
        mountOnIntent(oldest.tile);
    }
}

/**
 * Mount Swiper once, on the first real interaction intent.
 *
 * @param {Element} tile
 * @returns {void}
 */
function mountOnIntent(tile) {
    let mounted = false;

    const mount = () => {
        if (mounted) return;
        mounted = true;
        tile.removeEventListener("pointerenter", mount);
        tile.removeEventListener("touchstart", mount);

        // lazy=false: the element is already in view by definition here.
        // Carousel's ?img= deep-link branch keys on il-location-carousel and
        // dereferences a querySelector result without a null check, so it is
        // only safe to reach when the parameter is absent.
        //
        // No custom resize handling: Swiper's own onResize is bound with
        // priority and already re-aligns to the active slide for the new
        // geometry. A `resize` listener added here is appended, so it runs
        // AFTER that and would overwrite a correct position with a stale
        // pre-resize pixel offset — and setTranslate does not clamp, so a
        // widened viewport could leave the tile scrolled past its last slide.
        // The reset this replaced came from the per-element observer, which
        // tileOptions turns off.
        const carousel = new Carousel(tile, false, true, tileOptions(tile));
        mountedTiles.push({ tile, carousel });
        retireOverflow();
    };

    tile.addEventListener("pointerenter", mount, { once: false });
    tile.addEventListener("touchstart", mount, { passive: true });
}

/**
 * @param {Element} tile
 * @returns {void}
 */
/**
 * @param {Element} tile
 * @param {IntersectionObserver} observer so a throttled tile can be retried
 * @returns {void}
 */
function upgradeTile(tile, observer) {
    if (tile.dataset.tileFetching || tile.dataset.tileUpgraded) return;
    tile.dataset.tileFetching = "1";

    const slug = tile.dataset.locationSlug;
    // Event context is the page's, carried on the tile — the fragment must
    // agree with the tile the user is looking at, not decide for itself.
    const query = tile.dataset.event === "1" ? "?event=1" : "";

    fetch(`/location-library/tile/${encodeURIComponent(slug)}/slides${query}`, {
        headers: { Accept: "text/html" },
    })
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((html) => {
            if (!html.trim()) return; // no gallery — keep the static tile
            injectSlides(tile, html);
            mountOnIntent(tile);
        })
        .catch((status) => {
            // A throttled tile is not a dead tile: re-observe so it upgrades
            // on a later pass instead of latching off for the page's life.
            // Everything else stays silent — the static feature image is
            // still there, which is what a no-JS visitor sees anyway.
            if (status === 429 && observer) {
                observer.observe(tile);
            }
        })
        .finally(() => {
            delete tile.dataset.tileFetching;
        });
}

function init() {
    const tiles = document.querySelectorAll(TILE_SELECTOR);
    if (!tiles.length) return;

    // Swiper's layout CSS lives in the deferred app stylesheet; Carousel
    // only requests it a second AFTER init, which would guarantee a visible
    // reflow on every tile. Ask for it before anything is injected.
    loadStylesheet();

    // Reveal the JS-only controls now that JS is demonstrably running. The
    // magnifier ships with [hidden] so a no-JS visitor never sees a dead
    // button; removing the attribute is the only thing that shows it. Done
    // before the observer so the modal works even where
    // IntersectionObserver is missing.
    document.querySelectorAll(".il-tile-gallery-trigger[hidden]").forEach((btn) => {
        btn.removeAttribute("hidden");
    });

    // Magnifier → whole-gallery quick-look modal.
    document.addEventListener("click", (event) => {
        const trigger = event.target.closest(".il-tile-gallery-trigger");
        if (!trigger) return;

        event.preventDefault();
        openGalleryModal(trigger);
    });

    if (!("IntersectionObserver" in window)) {
        return; // swipe upgrade is optional; the static grid stands on its own
    }

    // Phones keep the static tile: one photo, and tapping it goes to the
    // location.
    //
    // The swipe upgrade is a pointer affordance. On a touch screen it fought
    // the two things a visitor actually wants from a grid — the mount fired
    // on `touchstart`, so a finger that was starting a page scroll converted
    // the tile into a gallery, and a horizontal drag then competed with
    // scrolling instead of following the link. Not fetching below the
    // breakpoint also spares phones ~24 fragment requests per page view.
    //
    // 768px is Bootstrap's md, the same boundary the rest of this template
    // uses. Evaluated once: a device that crosses it mid-session (rotation)
    // keeps whatever it started with rather than re-wiring under the visitor.
    // The magnifier and its full-gallery modal stay wired up at every width —
    // they are an explicit tap, not a gesture, and were deliberately made to
    // work on phones.
    if (!window.matchMedia("(min-width: 768px)").matches) {
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                observer.unobserve(entry.target);
                upgradeTile(entry.target, observer);
            });
        },
        { rootMargin: "200px" }
    );

    tiles.forEach((tile) => observer.observe(tile));
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}
